import { GOLD, ACCENT_GOLD, TEXT_PRIMARY, TEXT_BODY, TEXT_MUTED } from '../../lib/medievalTheme';

// Shared Discord-style popover primitives. Dark-glass card, thin
// gold border, hover-lightens rows, optional radio-dot indicator
// for single-select lists, and a 1px divider between rows.

export function PopoverPanel({ children, style, innerRef, ...rest }) {
  return (
    <div
      ref={innerRef}
      style={{
        background: 'rgba(10,8,6,0.98)',
        border: `1px solid ${GOLD} 0.3)`,
        borderRadius: 'var(--radius-xl)',
        boxShadow: '0 8px 24px rgba(0,0,0,0.55)',
        padding: 6,
        // No `overflow: hidden` here. The presence picker chains a
        // second PopoverPanel out the RIGHT side of this one, and
        // clipping would hide it entirely. Inner padding keeps rows
        // from touching the outer radius anyway, so the corners
        // stay clean without it.
        ...style,
      }}
      onClick={(e) => e.stopPropagation()}
      {...rest}
    >
      {children}
    </div>
  );
}

// Single menu row. `selected === boolean` turns on the radio-dot
// indicator (for "pick exactly one" lists). `leading` renders a
// coloured dot / icon on the left; `trailing` renders a chevron or
// secondary affordance on the right.
export function PopoverMenuItem({
  label, caption, leading, trailing, selected,
  active = false, disabled = false, onClick,
}) {
  const isRadio = typeof selected === 'boolean';
  const baseBg = active ? `${GOLD} 0.14)` : 'transparent';
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={(e) => {
        e.stopPropagation();
        if (!disabled) onClick?.(e);
      }}
      className="flex items-center justify-between gap-3 w-full text-left"
      style={{
        padding: '0.5625cqw 0.75cqw',
        borderRadius: 'var(--radius-md)',
        background: baseBg,
        color: disabled ? TEXT_MUTED : TEXT_PRIMARY,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
      }}
      onMouseDown={(e) => { if (!disabled) e.currentTarget.style.background = `${GOLD} 0.22)`; }}
      onMouseUp={(e) => { if (!disabled) e.currentTarget.style.background = `${GOLD} 0.1)`; }}
      onMouseEnter={(e) => { if (!disabled) e.currentTarget.style.background = `${GOLD} 0.1)`; }}
      onMouseLeave={(e) => { if (!disabled) e.currentTarget.style.background = baseBg; }}
    >
      <span className="flex items-center gap-2.5 text-[0.75cqw] truncate flex-1 min-w-0">
        {leading ? <span className="shrink-0">{leading}</span> : null}
        <span className="flex flex-col min-w-0 flex-1">
          <span className="truncate">{label}</span>
          {caption ? (
            <span className="text-[0.625cqw] truncate" style={{ color: TEXT_MUTED }}>
              {caption}
            </span>
          ) : null}
        </span>
      </span>
      {trailing ? <span className="shrink-0" style={{ color: TEXT_MUTED }}>{trailing}</span> : null}
      {isRadio ? (
        <span
          className="shrink-0 rounded-full"
          style={{
            width: '0.875cqw',
            height: '0.875cqw',
            border: `1.5px solid ${selected ? ACCENT_GOLD : 'rgba(200,180,120,0.35)'}`,
            background: selected ? ACCENT_GOLD : 'transparent',
            boxShadow: selected ? `0 0 6px ${GOLD} 0.4)` : 'none',
            transition: 'background 120ms, border-color 120ms',
          }}
        />
      ) : null}
    </button>
  );
}

export function PopoverMenuDivider() {
  return <div style={{ height: 1, background: `${GOLD} 0.1)`, margin: '0 0.5cqw' }} />;
}

// Simple section header inside a popover. Used when one panel lists
// options for a chosen category (e.g. "Idle → 15 Minutes / 1 Hour…").
export function PopoverMenuHeader({ children }) {
  return (
    <div
      className="px-3 py-1.5 text-[0.625cqw] font-semibold"
      style={{ color: ACCENT_GOLD }}
    >
      {children}
    </div>
  );
}

// Right-pointing chevron suitable for "has submenu" rows.
export function SubmenuChevron() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" style={{ width: '0.625cqw', height: '0.625cqw' }}>
      <path d="M9 6l6 6-6 6" />
    </svg>
  );
}

export const POPOVER_TEXT = { TEXT_PRIMARY, TEXT_BODY, TEXT_MUTED, ACCENT_GOLD };
